import client from "./client";
import { getWebDeviceCode } from "../utils/deviceCode";

export interface UserMe {
  /** The user id (32 lowercase hex or a ULID); same value as the access-token `sub`. */
  id: string;
  username: string;
  email: string;
  locale: string;
  avatar: string | null;
  hasInformation?: boolean;
  pubnubGroupChannel: string;
}

export async function getMe(): Promise<UserMe> {
  const res = await client.get<{ result: UserMe }>("/api/user/me");
  return res.data.result;
}

/**
 * Presence heartbeat. main-api stamps a user online on every authenticated
 * request, but the periodic street-chat polls are now served by room-stc,
 * which never marks users online. Idle tabs call this once per poll tick.
 * Fire-and-forget: the result and any error are ignored.
 */
export async function touchPresence(): Promise<void> {
  try {
    await client.get("/api/user/me");
  } catch {
    // best effort
  }
}

export async function updateMe(
  data: Partial<Pick<UserMe, "locale">> & Record<string, unknown>,
): Promise<UserMe> {
  const res = await client.patch<{ result: UserMe }>("/api/user/me", data);
  return res.data.result;
}

export async function getUserChannel(): Promise<string> {
  const deviceCode = getWebDeviceCode();
  // Served by room-stc (migrated from main-api; same path and JSON).
  const res = await client.get<{ result: string }>(
    `/room/api/user/channel?deviceCode=${encodeURIComponent(deviceCode)}`,
  );
  return res.data.result;
}

/**
 * The map squares (geohash level 4) this tab's device queue watches for presence (spec 2026-10-31 §2). room-stc
 * answers 200 `true`; 400 = bad input, 404 = the device queue is gone (run the channel request again), 503 = broker
 * trouble (retry). `deviceCode` must be the one the channel request used (`getWebDeviceCode()`).
 */
export async function putChannelArea(deviceCode: string, cells: string[]): Promise<void> {
  await client.put("/room/api/user/channel/area", { deviceCode, cells });
}

/** One row of GET /api/user/search (SimpleUserResponse). `id` and `userId` are the same value. */
export interface SearchUser {
  id: string;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  name: string | null;
  avatar: string | null;
}

/** Search people by email. The backend returns nothing for terms under 3 chars. */
export async function searchUsers(email: string): Promise<SearchUser[]> {
  const res = await client.get<{ result: SearchUser[] | null }>(
    "/api/user/search",
    { params: { email } },
  );
  return res.data.result ?? [];
}

/** A person found by name (main-api UserSearchResult, OpenSearch over first / last name and talk about). */
export interface NameSearchUser {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  talkAbout: string | null;
}

/** Search people by name (main-api GET /api/search/users). A blank q is a 400: callers send trimmed, non-blank text. */
export async function searchUsersByName(q: string): Promise<NameSearchUser[]> {
  const res = await client.get<{ result: NameSearchUser[] | null }>("/api/search/users", { params: { q } });
  return res.data.result ?? [];
}
